<template>
  <div v-if="authStore.isInSwitchedOrg" class="org-banner" role="alert">
    <div class="org-banner-inner">
      <div class="org-banner-icon-wrap">
        <Building2 :size="18" />
      </div>

      <div class="org-banner-text">
        <span class="org-banner-title">{{ $gt('Organisation gewechselt') }}</span>
        <span class="org-banner-desc">
          {{ $gt('Sie arbeiten aktuell in der Organisation') }}
          <strong>{{ activeOrgName }}</strong
          ><span v-if="primaryOrgName">{{ ' ' + $gt('statt in Ihrer Stammorganisation') }} <strong>{{ primaryOrgName }}</strong></span>.
        </span>
      </div>

      <button
        v-if="primaryOrgId"
        :aria-label="$gt('Zurück zur Stammorganisation')"
        class="org-banner-back-btn"
        :disabled="switching"
        :title="$gt('Zurück zur Stammorganisation')"
        @click="handleBack"
      >
        <RotateCcw :size="14" />
        <span class="org-banner-back-label">{{ switching ? "Wechsle..." : "Zur\u00fcck zur Stammorganisation" }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { Building2, RotateCcw } from "lucide-vue-next";
import { useAuthStore } from "@/stores/authStore";

export default {
  name: "ActiveOrgBanner",

  components: { Building2, RotateCcw },

  setup() {
    return { authStore: useAuthStore() };
  },

  data() {
    return { switching: false };
  },

  computed: {
    activeOrgName() {
      return this.authStore.activeLinkedOrg?.organizationName || this.authStore.organizationName || "";
    },
    primaryOrgName() {
      return this.authStore.primaryLinkedOrg?.organizationName || "";
    },
    primaryOrgId() {
      return this.authStore.primaryLinkedOrg?.idOrganization || 0;
    },
  },

  methods: {
    async handleBack() {
      if (this.switching || !this.primaryOrgId) return;
      this.switching = true;
      try {
        await this.authStore.switchOrg(this.primaryOrgId);
      } finally {
        this.switching = false;
      }
    },
  },
};
</script>

<style scoped>
/* Feste Höhe: App.vue schiebt die App-Leisten um genau 44px nach unten
   (--app-top-banner-offset). Steht zusätzlich das Banner der SuperAdmin-
   Sitzung oben, liegt dieses Banner darunter (--app-org-banner-top). */
.org-banner {
  position: fixed;
  top: var(--app-org-banner-top, 0px);
  left: 0;
  right: 0;
  z-index: 9998;
  background: linear-gradient(90deg, #1e40af 0%, #2563eb 100%);
  color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  font-size: 0.825rem;
  line-height: 1.35;
  height: 44px;
  overflow: hidden;
}

.org-banner-inner {
  max-width: 1400px;
  height: 100%;
  margin: 0 auto;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.org-banner-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  flex-shrink: 0;
}

.org-banner-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.org-banner-title {
  font-weight: 600;
  letter-spacing: 0.02em;
  font-size: 0.7rem;
  text-transform: uppercase;
  opacity: 0.9;
}

.org-banner-desc {
  font-size: 0.825rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.org-banner-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}

.org-banner-back-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.24);
}

.org-banner-back-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Schmal: Hinweis einzeilig gekürzt, Zurück-Schaltfläche nur als Symbol,
   damit die Leiste bei 44px bleibt. */
@media (max-width: 640px) {
  .org-banner-inner {
    gap: 8px;
    padding: 0 12px;
  }

  .org-banner-icon-wrap {
    display: none;
  }

  .org-banner-back-label {
    display: none;
  }

  .org-banner-back-btn {
    padding: 8px;
  }
}
</style>
